<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb :items="[{ text: 'Taxonomies', path: {name: 'admin-taxonomies'} }]" name="Strain Types" no-title/>
    <b-row>
      <b-col cols="12">
        <admin-panel title="Strain Types">
          <template v-slot:header-right>
            <no-permission-wrapper :show="!canCreateTaxonomies">
              <b-btn variant="success" @click="openNewModal" :disabled="!canCreateTaxonomies">Add new Strain Type</b-btn>
            </no-permission-wrapper>
          </template>
          <b-table
              :fields="table_fields"
              :items="strainTypes"
              table-class="text-nowrap"
              responsive
          >
            <template #cell(name)="data">
              <h4 class="mb-0">{{ data.item.name }}</h4>
            </template>
            <template #cell(actions)="data">
              <no-permission-wrapper :show="!canUpdateTaxonomies">
                <b-btn variant="primary" @click.prevent="openEditModal(data.item)" :disabled="!canUpdateTaxonomies">Edit</b-btn>
              </no-permission-wrapper>
              <no-permission-wrapper :show="!canDeleteTaxonomies">
                <b-btn variant="danger" @click.prevent="confirmDelete(data.item)" :disabled="!canDeleteTaxonomies">Delete</b-btn>
              </no-permission-wrapper>
            </template>
            <template #cell(order)="data">
              <no-permission-wrapper :show="!canUpdateTaxonomies">
                <b-button v-if="data.index > 0" @click="changeOrder(data.index, data.index - 1)" :disabled="!canUpdateTaxonomies">
                  <b-icon-arrow-up/>
                </b-button>
                <b-button v-if="data.index < strainTypes.length - 1" @click="changeOrder(data.index, data.index + 1)" :disabled="!canUpdateTaxonomies">
                  <b-icon-arrow-down/>
                </b-button>
              </no-permission-wrapper>
            </template>
          </b-table>
        </admin-panel>
      </b-col>
    </b-row>
    <admin-strain-type-edit-modal
        v-if="edit_strain_type"
        :strain-type="edit_strain_type"
        @cancel="edit_strain_type = null"
        @success="onStrainTypeEdited"
        visible
    />
    <admin-strain-type-add-modal
        ref="newModal"
        @success="onStrainTypeAdded"
    />
  </b-container>
</template>

<script>
import AdminPanel from "../components/AdminPanel";
import {API_ROUTES} from "../api";
import AdminStrainTypeEditModal from "../components/AdminStrainTypeEditModal";
import AdminStrainTypeAddModal from "../components/AdminStrainTypeAddModal";
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import NoPermissionWrapper from "../components/NoPermissionWrapper.vue";

export default {
  name: "AdminStrainTypes",
  components: {
    NoPermissionWrapper,
    AdminBreadcrumb, AdminPreloader, AdminStrainTypeAddModal, AdminStrainTypeEditModal, AdminPanel},
  data() {
    return {
      loading: false,
      table_fields: [
        {
          key: "order",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "name",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "actions",
          thClass: "border-top-0",
          tdClass: "align-middle"
        }
      ],
      edit_strain_type: null
    }
  },
  computed: {
    canCreateTaxonomies() {
      return this.$store.getters['auth/hasAdminPermissions']('taxonomy_create');
    },
    canUpdateTaxonomies() {
      return this.$store.getters['auth/hasAdminPermissions']('taxonomy_update');
    },
    canDeleteTaxonomies() {
      return this.$store.getters['auth/hasAdminPermissions']('taxonomy_delete');
    },
    search() {
      return this.$store.state.current.search;
    },
    strainTypes() {
      let strainTypes = this.$store.state.data.strain_types;
      if (this.search) {
        let search = this.search.toLowerCase();
        strainTypes = strainTypes.filter(st => st.name.toLowerCase().indexOf(search) >= 0);
      }

      return strainTypes;
    }
  },
  methods: {
    checkPermission(permission) {
      if (!this[permission]) {
        this.$bvToast.toast('Sorry! You don\'t have permission to do this action', {
          title: 'Denied!',
          variant: 'danger'
        });
        return false;
      }
      return true;
    },
    openNewModal() {
      if (!this.checkPermission('canCreateTaxonomies')) {
        return;
      }
      this.$refs.newModal.open();
    },
    openEditModal(strainType) {
      if (!this.checkPermission('canUpdateTaxonomies')) {
        return;
      }
      this.edit_strain_type = strainType;
    },
    confirmDelete(strainType) {
      if (!this.checkPermission('canDeleteTaxonomies')) {
        return;
      }
      this.$bvModal.msgBoxConfirm(`You are going to delete ${strainType.name} strain type`, {
        title: 'Please confirm',
        okVariant: 'success',
        centered: true
      }).then(value => {
        if (value) {
          this.loading = true;
          this.$api.post(`${API_ROUTES.ADMIN_TAXONOMY_STRAIN_TYPES}/${strainType.id}/delete`).then(() => {
            let index = this.strainTypes.findIndex(st => st.id === strainType.id);
            if (index >= 0) {
              let strainTypes = [...this.strainTypes];
              strainTypes.splice(index, 1);
              this.$store.commit('data/setStrainTypes', strainTypes);
            }
            this.$bvToast.toast('Strain type has been successfully deleted', {
              title: 'Success!',
              variant: 'success'
            });
            this.formValidation = {};
          }).catch(({response: {data}}) => {
            this.$bvToast.toast(data.message ?? 'Failed to process operation', {
              title: 'Error!',
              variant: 'danger'
            });
          }).finally(() => {
            this.loading = false;
          });
        }
      });
    },
    onStrainTypeAdded(strainType) {
      this.$bvToast.toast('Strain type has been successfully created', {
        title: 'Success!',
        variant: 'success'
      });
      let strainTypes = [...this.strainTypes];
      strainTypes.push(strainType);
      this.$store.commit('data/setStrainTypes', strainTypes);
    },
    onStrainTypeEdited(strainType) {
      this.$bvToast.toast('Strain type has been successfully updated', {
        title: 'Success!',
        variant: 'success'
      });
      let strainTypes = [...this.strainTypes];
      let index = strainTypes.findIndex(ct => ct.id === strainType.id);
      if (index >= 0) {
        strainTypes[index] = strainType;
      } else {
        strainTypes.push(strainType);
      }
      this.$store.commit('data/setStrainTypes', strainTypes);
    },
    changeOrder(indexOne, indexTwo) {
      let itemOne = this.strainTypes[indexOne],
          itemTwo = this.strainTypes[indexTwo];
      if (itemOne && itemTwo) {
        this.loading = true;
        this.$api.post(`${API_ROUTES.ADMIN_TAXONOMY_STRAIN_TYPES}/change-order`, {
          first: itemOne.id,
          second: itemTwo.id
        }).then(({data}) => {
          this.$store.commit('data/setStrainTypes', data.strain_types);
        }).catch(() => {

        }).finally(() => {
          this.loading = false;
        })
      }
    }
  }
}
</script>
